/**
 * URLDataParser - Parses BASE64-encoded JSON data from URL query parameters
 * 
 * URL Format: http://localhost:5173/#formName?data=BASE64_ENCODED_JSON
 * 
 * Example:
 *   URL: http://localhost:5173/#samples/suno?data=eyJQbGF5bGlzdCI6Imh0dHBzOi8vc3Vuby5jb20vcGxheWxpc3QvYjhiYjY0ZGItOTYyMC00ZDgzLTk3MTctOWI0YTg3MDVkZDM5IiwiQXV0b0xvYWQiOnRydWUsIkF1dG9QbGF5Ijp0cnVlfQ==
 *   
 *   Decodes to:
 *   {
 *     "Playlist": "https://suno.com/playlist/b8bb64db-9620-4d83-9717-9b4a8705dd39",
 *     "AutoLoad": true,
 *     "AutoPlay": true
 *   }
 * 
 * This approach keeps URLs readable (form name is visible) while supporting
 * complex data including URLs and special characters via BASE64 encoding.
 */

import { Logger } from './Logger';

const logger = Logger.scope('URLDataParser');

/**
 * Result of parsing the URL hash
 */
export interface ParsedURL {
  /** The form name from the hash (e.g., "samples/suno") */
  formName: string;
  /** Decoded data object from the ?data= parameter, or null if none */
  data: Record<string, unknown> | null;
  /** Raw BASE64 string if present (for debugging) */
  rawData: string | null;
}

/**
 * Parse the current URL hash and extract form name and data
 * 
 * Handles formats:
 *   #formName
 *   #formName?data=BASE64
 *   #path/to/form?data=BASE64
 */
export function parseCurrentURL(): ParsedURL {
  return parseURLHash(window.location.hash);
}

/**
 * Parse a URL hash string and extract form name and data
 * 
 * @param hash - The hash portion of the URL (with or without leading #)
 */
export function parseURLHash(hash: string): ParsedURL {
  // Remove leading # if present
  const hashContent = hash.startsWith('#') ? hash.substring(1) : hash;
  
  if (!hashContent) {
    return { formName: '', data: null, rawData: null };
  }

  // Split on ? to separate form name from query string
  const [formPart, queryPart] = hashContent.split('?');
  
  const result: ParsedURL = {
    formName: formPart || '',
    data: null,
    rawData: null
  };

  // Parse query parameters if present
  if (queryPart) {
    const params = new URLSearchParams(queryPart);
    const dataParam = params.get('data');
    
    if (dataParam) {
      result.rawData = dataParam;
      result.data = decodeBase64JSON(dataParam);
    }
  }

  return result;
}

/**
 * Decode a BASE64-encoded JSON string
 * 
 * @param base64 - BASE64-encoded JSON string
 * @returns Decoded object, or null if decoding fails
 */
export function decodeBase64JSON(base64: string): Record<string, unknown> | null {
  try {
    // Decode BASE64 to string
    const jsonString = atob(base64);
    logger.debug('Decoded BASE64 JSON:', jsonString);
    
    // Parse JSON
    const data = JSON.parse(jsonString);
    return data;
  } catch (error) {
    logger.error('Failed to decode BASE64 JSON data:', error);
    return null;
  }
}

/**
 * Encode an object to BASE64 JSON for use in URLs
 * 
 * @param data - Object to encode
 * @returns BASE64-encoded JSON string
 */
export function encodeBase64JSON(data: Record<string, unknown>): string {
  const jsonString = JSON.stringify(data);
  return btoa(jsonString);
}

/**
 * Build a complete URL hash with encoded data
 * 
 * @param formName - The form to navigate to
 * @param data - Optional data object to pass to the form
 * @returns Hash string (without leading #)
 */
export function buildURLHash(formName: string, data?: Record<string, unknown>): string {
  if (!data || Object.keys(data).length === 0) {
    return formName;
  }
  
  const encoded = encodeBase64JSON(data);
  return `${formName}?data=${encoded}`;
}

/**
 * Navigate to a form with optional data
 * Updates the URL hash which will trigger form loading
 * 
 * @param formName - The form to navigate to
 * @param data - Optional data object to pass to the form
 */
export function navigateWithData(formName: string, data?: Record<string, unknown>): void {
  const hash = buildURLHash(formName, data);
  logger.debug('Navigating to:', hash);
  window.location.hash = hash;
}

/**
 * Get just the form name from the current URL (stripping any query params)
 */
export function getFormNameFromURL(): string | null {
  const parsed = parseCurrentURL();
  return parsed.formName || null;
}

/**
 * Get the data from the current URL
 */
export function getURLData<T = Record<string, unknown>>(): T | null {
  const parsed = parseCurrentURL();
  return parsed.data as T | null;
}
